import { useCallback, useEffect } from "react";
import type { DocumentViewEvent, PageViewEventData } from "../../../api/clients/documentEventsApi";
import documentEventsApi from "../../../api/clients/documentEventsApi";
import useEventListener from "../../../hooks/useEventListener";
import useSetInterval from "../../../hooks/useSetInterval";
import { logError } from "../../../logging";
import { pageViewTracker } from "./pageViewTracker";

interface PageViewTrackingHookParams {
  sessionId: string | undefined;
  numPages: number;
  videoFileDurationSec?: number;
  visiblePages: number[];
  pauseTracking?: boolean;
}

const persistPageViews = async (pageViews: DocumentViewEvent<PageViewEventData>[], keepalive?: boolean) => {
  if (pageViews.length === 0) {
    return;
  }

  const ingest = keepalive
    ? documentEventsApi.ingestDocumentPageViewEventsKeepalive
    : documentEventsApi.ingestDocumentPageViewEvents;

  try {
    await ingest({ viewEventsData: pageViews });
  } catch (error) {
    logError(error, "[PageViewTracker]");
  }
};

const usePageViewTracking = ({
  sessionId,
  numPages,
  videoFileDurationSec,
  visiblePages,
  pauseTracking,
}: PageViewTrackingHookParams) => {
  useEffect(() => {
    if (sessionId === undefined) {
      return;
    }

    if (numPages > 0) {
      pageViewTracker.startSession(sessionId, numPages, videoFileDurationSec);
    }

    return () => {
      persistPageViews(pageViewTracker.eventsBufferToPageViews());
      pageViewTracker.stopSession();
    };
  }, [numPages, sessionId, videoFileDurationSec]);

  useEffect(() => {
    if (sessionId === undefined) {
      return;
    }

    if (pauseTracking) {
      pageViewTracker.trackViewStop();
    } else {
      pageViewTracker.trackViewStart(visiblePages);
    }
  }, [visiblePages, pauseTracking, sessionId]);

  const onTick = useCallback(() => {
    if (sessionId === undefined) {
      return;
    }

    persistPageViews(pageViewTracker.eventsBufferToPageViews());
  }, [sessionId]);

  useSetInterval(onTick, 12_000);

  // Mobile browsers may discard a hidden page without firing pagehide, so hidden is the last reliable flush point
  const handleVisibilityChange = useCallback(() => {
    if (sessionId === undefined || !document.hidden) {
      return;
    }

    persistPageViews(pageViewTracker.eventsBufferToPageViews(), true);
  }, [sessionId]);

  useEventListener(document, "visibilitychange", handleVisibilityChange);
};

export default usePageViewTracking;
